/* eslint-disable max-len */
import { API } from 'aws-amplify';
import {
	ContentType,
	ContentFilterInput,
	ContentSortInput,
	TimeRangeInput
} from './content_graphql_types';

// Main GraphQL queries based on the documentation
const COMPANY_CONTENT_QUERY = `
query GetAllContent($companyId: ID!, $filters: ContentFilterInput, $page: Int, $limit: Int, $sort: ContentSortInput, $from: String!) {
  companyContent(companyId: $companyId, filters: $filters, page: $page, limit: $limit, sort: $sort, from: $from) {
    success
    data {
      ... on Article { _id title summary articleType categoryIds subCategory tags status visibility author_id createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Video { _id videos { title description status duration fileSize uploadedAt } categoryIds subCategory tags visibility createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Podcast { _id title description categoryIds subCategory tags status visibility createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Image { _id files { title description status fileSize uploadedAt categoryIds } subCategory tags createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Document { _id files { title description status fileSize uploadedAt categoryIds } subCategory tags createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Playlist { _id name description items { title contentType addedAt addedBy { _id name } } visibility enabled allowShare allowDownload views contentCount createdAt updatedAt createdBy { _id name } updatedBy { _id name } }
      ... on Category { _id name description companyId companyName isEnabled visibility allowDownloads allowShare parentCategoryId isSubcategory level path subcategoryCount viewCount createdAt updatedAt createdBy { _id name } updatedBy { _id name } }
    }
    pagination {
      total
      page
      limit
      pages
    }
  }
}`;

const SEARCH_CONTENT_QUERY = `
query SearchContent($companyId: ID!, $searchTerm: String!, $filters: ContentFilterInput, $page: Int, $limit: Int, $sort: ContentSortInput, $from: String!) {
  searchContent(companyId: $companyId, searchTerm: $searchTerm, filters: $filters, page: $page, limit: $limit, sort: $sort, from: $from) {
    success
    data {
      ... on Article { _id title summary articleType categoryIds subCategory tags status visibility author_id createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Video { _id videos { title description status duration fileSize uploadedAt } categoryIds subCategory tags visibility createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Podcast { _id title description categoryIds subCategory tags status visibility createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Image { _id files { title description status fileSize uploadedAt categoryIds } subCategory tags createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Document { _id files { title description status fileSize uploadedAt categoryIds } subCategory tags createdAt updatedAt publishedAt createdBy { _id name } updatedBy { _id name } publishedBy { _id name } engagement { views downloads helpful notHelpful } }
      ... on Playlist { _id name description items { title contentType addedAt addedBy { _id name } } visibility enabled allowShare allowDownload views contentCount createdAt updatedAt createdBy { _id name } updatedBy { _id name } }
      ... on Category { _id name description companyId companyName isEnabled visibility allowDownloads allowShare parentCategoryId isSubcategory level path subcategoryCount viewCount createdAt updatedAt createdBy { _id name } updatedBy { _id name } }
    }
    pagination {
      total
      page
      limit
      pages
    }
  }
}`;

const CONTENT_STATS_QUERY = `
query ContentStatistics($companyId: ID!, $timeRange: TimeRangeInput, $createdBy: ID, $from: String!) {
  contentStats(companyId: $companyId, timeRange: $timeRange, createdBy: $createdBy, from: $from) {
    success
    message
    data {
      categorized { count previousCount }
      uncategorized { count previousCount }
      draft { count previousCount }
      published { count previousCount }
      scheduled { count previousCount }
      unpublished { count previousCount }
      total
    }
  }
}`;

const MOST_VIEWED_CONTENT_QUERY = `
query MostViewedContent($companyId: ID!, $limit: Int, $contentTypes: [String], $timeRange: TimeRangeInput, $createdBy: ID, $from: String!) {
  mostViewedContent(companyId: $companyId, limit: $limit, contentTypes: $contentTypes, timeRange: $timeRange, createdBy: $createdBy, from: $from) {
    success
    message
    data {
      ... on Article { _id title engagement { views downloads } createdAt createdBy { _id name } updatedBy { _id name } }
      ... on Video { _id videos { title } engagement { views downloads } createdAt createdBy { _id name } updatedBy { _id name } }
      ... on Podcast { _id title engagement { views downloads } createdAt createdBy { _id name } updatedBy { _id name } }
      ... on Image { _id files { title } engagement { views downloads } createdAt createdBy { _id name } updatedBy { _id name } }
      ... on Document { _id files { title } engagement { views downloads } createdAt createdBy { _id name } updatedBy { _id name } }
      ... on Playlist { _id name items { title } createdAt createdBy { _id name } updatedBy { _id name } }
      ... on Category { _id name description createdAt createdBy { _id name } updatedBy { _id name } }
    }
  }
}`;

const CONTENT_COUNT_QUERY = `
query GetContentCounts($companyId: ID!, $filters: ContentFilterInput, $createdBy: ID, $from: String!) {
  companyContent(companyId: $companyId, filters: $filters, createdBy: $createdBy, from: $from) {
    success
    counts {
      articles
      videos
      podcasts
      images
      documents
      playlists
      categories
      total
    }
  }
}`;

/**
 * Fetches all content for a specific company with optional filtering and sorting
 *
 * @param companyId Company ID
 * @param from Source parameter ('MyResource' or 'Configuration')
 * @param options Optional parameters for filtering, pagination, and sorting
 * @returns Promise resolving to content response
 */
const getCompanyContent = async (
	companyId: string,
	from: 'MyResource' | 'Configuration',
	options: {
    filters?: ContentFilterInput;
    page?: number;
    limit?: number;
    sort?: ContentSortInput;
  } = {}
) =>
{
	try
	{
		const { filters = {}, page = 1, limit = 10, sort } = options;

		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query     : COMPANY_CONTENT_QUERY,
				variables : {
					companyId,
					filters,
					page,
					limit,
					sort,
					from
				}
			}
		});

		return response.data.companyContent;
	}
	catch (error)
	{
		console.error('Error fetching company content:', error);
		throw error;
	}
};

/**
 * Searches content across all types for a specific company
 *
 * @param companyId Company ID
 * @param searchTerm Search term (must be at least 3 characters)
 * @param from Source parameter ('MyResource' or 'Configuration')
 * @param options Optional parameters for filtering, pagination, and sorting
 * @returns Promise resolving to search results
 */
const searchContent = async (
	companyId: string,
	searchTerm: string,
	from: 'MyResource' | 'Configuration',
	options: {
    filters?: ContentFilterInput;
    page?: number;
    limit?: number;
    sort?: ContentSortInput;
  } = {}
) =>
{
	if (searchTerm.length < 3)
	{
		throw new Error('Search term must be at least 3 characters long');
	}

	try
	{
		const { filters = {}, page = 1, limit = 10, sort } = options;

		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query     : SEARCH_CONTENT_QUERY,
				variables : {
					companyId,
					searchTerm,
					filters,
					page,
					limit,
					sort,
					from
				}
			}
		});

		return response.data.searchContent;
	}
	catch (error)
	{
		console.error('Error searching content:', error);
		throw error;
	}
};

/**
 * Gets content statistics categorized by status
 *
 * @param companyId Company ID
 * @param from Source parameter ('MyResource' or 'Configuration')
 * @param timeRange Optional time range for filtering statistics
 * @returns Promise resolving to content statistics
 */
const getContentStats = async (
	companyId: string,
	from: 'MyResource' | 'Configuration',
	timeRange?: TimeRangeInput
) =>
{
	try
	{
		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query     : CONTENT_STATS_QUERY,
				variables : {
					companyId,
					timeRange,
					from
				}
			}
		});

		return response.data.contentStats;
	}
	catch (error)
	{
		console.error('Error fetching content statistics:', error);
		throw error;
	}
};

/**
 * Gets the most viewed content across specified content types
 *
 * @param companyId Company ID
 * @param from Source parameter ('MyResource' or 'Configuration')
 * @param options Optional parameters for filtering and limiting results
 * @returns Promise resolving to most viewed content data
 */
const getMostViewedContent = async (
	companyId: string,
	from: 'MyResource' | 'Configuration',
	options: {
    limit?: number;
    contentTypes?: ContentType[];
    timeRange?: TimeRangeInput;
  } = {}
) =>
{
	try
	{
		const { limit = 10, contentTypes, timeRange } = options;

		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query     : MOST_VIEWED_CONTENT_QUERY,
				variables : {
					companyId,
					limit,
					contentTypes,
					timeRange,
					from
				}
			}
		});

		return response.data.mostViewedContent;
	}
	catch (error)
	{
		console.error('Error fetching most viewed content:', error);
		throw error;
	}
};

/**
 * Gets counts of different content types
 *
 * @param companyId Company ID
 * @param from Source parameter ('MyResource' or 'Configuration')
 * @param filters Optional filters to apply to the count
 * @returns Promise resolving to content count data
 */
const getContentCounts = async (
	companyId: string,
	from: 'MyResource' | 'Configuration',
	filters?: ContentFilterInput
) =>
{
	try
	{
		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query     : CONTENT_COUNT_QUERY,
				variables : {
					companyId,
					filters,
					from
				}
			}
		});

		return response.data.companyContentCounts;
	}
	catch (error)
	{
		console.error('Error fetching content counts:', error);
		throw error;
	}
};

/**
 * Gets separate counts for each content type using individual queries
 *
 * @param companyId Company ID
 * @param from Source parameter ('MyResource' or 'Configuration')
 * @param filters Optional base filters to apply to all content queries
 * @returns Promise resolving to separate content count data for each type
 */
const getSeparateContentCounts = async (
	companyId: string,
	from: 'MyResource' | 'Configuration',
	filters?: ContentFilterInput
) =>
{
	try
	{
		// Define mapping from content types to query field names
		const typeToFieldMapping: Record<string, string> = {
			ARTICLE  : 'articles',
			VIDEO    : 'videos',
			PODCAST  : 'podcasts',
			IMAGE    : 'images',
			DOCUMENT : 'documents',
			PLAYLIST : 'playlists',
			CATEGORY : 'categories'
		};

		// Get the content types from filters.types or default to all types
		const contentTypes: string[] = filters?.types || [ 'ARTICLE', 'VIDEO', 'PODCAST', 'IMAGE', 'DOCUMENT', 'PLAYLIST', 'CATEGORY' ];

		// Build dynamic query and variables
		let queryFields = '';
		// const variables: any = { companyId };

		// Always include allContent if we have any types
		if (contentTypes.length > 0)
		{
			queryFields += `
				allContent: companyContent(companyId: $companyId, filters: $filters, limit: 1, from: $from) {
					pagination {
						total
					}
				}`;
			// variables.allFilters = filters || {};
		}

		const subFilters = { };

		// Add fields for each content type in the filters.types array
		contentTypes.forEach((type) =>
		{
			const fieldName = typeToFieldMapping[type];

			if (fieldName)
			{
				const variableName = `${fieldName}Filters`;

				if (type === 'CATEGORY')
				{
					subFilters[variableName] = { ...filters };
					delete subFilters[variableName].categoryIds;
					delete subFilters[variableName].types;
					delete subFilters[variableName].nested;
					delete subFilters[variableName].status;
					if(from === 'MyResource') subFilters[variableName].isEnabled = true;
					if (filters?.categoryIds?.length) subFilters[variableName].parentCategoryId = filters.categoryIds[0];
					// if (Object.keys(filters?.timeRange || {})?.length) subFilters[variableName].timeRange = filters.timeRange;

					queryFields += `
						${fieldName}: categories(companyId: $companyId, filters: $${variableName}, limit: 1, from: $from) {
							pagination {
								total
							}
						}`;
				}
				else
				{
					// const subFilter = { ...filters, types: [ type ] };
					subFilters[variableName] = { ...filters, types: [ type ] };

					queryFields += `
						${fieldName}: companyContent(companyId: $companyId, filters: $${variableName}, limit: 1, from: $from) {
							pagination {
								total
							}
						}`;
				}
			}
		});

		const filterQuery = Object.keys(subFilters).map((key) => `$${key}: ${key === 'categoriesFilters' ? 'CategoryFilters' : 'ContentFilterInput'}`)
			.join(', ');

		const dynamicQuery = `
			query GetSeparateContentCounts($companyId: ID!, $filters: ContentFilterInput, $from: String!, ${filterQuery}) {
				${queryFields}
			}`;

		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query     : dynamicQuery,
				variables : {
					companyId,
					filters,
					from,
					...subFilters
				}
			}
		});

		// Build response object with only the requested content types
		const result: Record<string, number> = {};

		// Always include allContent if it exists
		if (response.data.allContent)
		{
			result.allContent = (response.data.allContent?.pagination?.total || 0) +
			(contentTypes?.includes('CATEGORY') ? (response.data.categories?.pagination?.total || 0) : 0);
		}

		// Add counts for each requested content type
		contentTypes.forEach((type) =>
		{
			const fieldName = typeToFieldMapping[type];

			if (fieldName && response.data[fieldName])
			{
				result[fieldName] = response.data[fieldName]?.pagination?.total || 0;
			}
		});

		// Ensure all possible fields are present with 0 as default
		const defaultResult = {
			allContent    : result.allContent || 0,
			articles      : result.articles || 0,
			videos        : result.videos || 0,
			podcasts      : result.podcasts || 0,
			images        : result.images || 0,
			documents     : result.documents || 0,
			playlists     : result.playlists || 0,
			categories    : result.categories || 0,
			subcategories : result.categories || 0
		};

		return defaultResult;
	}
	catch (error)
	{
		console.error('Error fetching separate content counts:', error);
		throw error;
	}
};

// Export all functions
export default {
	getCompanyContent,
	searchContent,
	getContentStats,
	getMostViewedContent,
	getContentCounts,
	getSeparateContentCounts
};